import React, { useState, useRef, useCallback } from 'react';
import { messagesAPI } from '../api';
import MessagePreset from './MessagePreset';
import StudentPreview from './StudentPreview';
import {
  Upload, Download, FileSpreadsheet, Send, CheckCircle2,
  XCircle, Loader2, MessageSquare, FileText, Layers
} from 'lucide-react';

const SEND_MODES = [
  {
    id: 'text',
    label: 'Text Only',
    icon: <MessageSquare size={20} />,
    desc: 'WhatsApp text message',
    color: 'border-blue-400 bg-blue-50',
    activeColor: 'border-blue-600 bg-blue-100 ring-2 ring-blue-400',
  },
  {
    id: 'pdf',
    label: 'PDF Only',
    icon: <FileText size={20} />,
    desc: 'PDF attachment only',
    color: 'border-purple-400 bg-purple-50',
    activeColor: 'border-purple-600 bg-purple-100 ring-2 ring-purple-400',
  },
  {
    id: 'both',
    label: 'Text + PDF',
    icon: <Layers size={20} />,
    desc: 'PDF card + text message',
    color: 'border-orange-400 bg-orange-50',
    activeColor: 'border-orange-500 bg-orange-100 ring-2 ring-orange-400',
  },
];

export default function SubjectMarksSender({ sessionId, schoolName }) {
  const [file, setFile] = useState(null);
  const [students, setStudents] = useState([]);
  const [sendMode, setSendMode] = useState('text');
  const [presetId, setPresetId] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) return;
    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      setError('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }
    setFile(selectedFile);
    setError('');
    setResult(null);

    // Parse preview
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const res = await messagesAPI.parseExcel(formData);
      setStudents(res.data.students || []);
    } catch (err) {
      setError('Failed to parse Excel file. Please use the provided template.');
      setStudents([]);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const handleDragLeave = () => setDragActive(false);

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await messagesAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'marks_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = async () => {
    if (!file) { setError('Please upload an Excel file first'); return; }
    if (students.length === 0) { setError('No student records found'); return; }

    setSending(true);
    setResult(null);
    setError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('sessionId', sessionId);
    formData.append('presetId', presetId || '');
    formData.append('sendMode', sendMode);
    formData.append('schoolName', schoolName || '');

    try {
      const res = await messagesAPI.sendMarks(formData);
      setResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send messages';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Upload Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <FileSpreadsheet size={18} className="text-blue-600" />
          Upload Excel File
        </h3>

        {/* Drag & Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
            dragActive
              ? 'border-blue-500 bg-blue-50 drag-active'
              : file
              ? 'border-green-400 bg-green-50'
              : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files[0])}
          />
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <CheckCircle2 size={36} className="text-green-500" />
              <p className="font-semibold text-green-700">{file.name}</p>
              <p className="text-sm text-green-600">{students.length} students loaded</p>
              <p className="text-xs text-gray-500">Click to change file</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <Upload size={36} className="text-gray-400" />
              <div>
                <p className="font-semibold text-gray-600">Drag & drop your Excel file here</p>
                <p className="text-sm text-gray-400 mt-1">or click to browse</p>
              </div>
              <button
                type="button"
                className="btn-3d-blue text-sm py-2 px-4 mt-1"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              >
                Choose File
              </button>
            </div>
          )}
        </div>

        {/* Download Template */}
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={handleDownloadTemplate}
            disabled={downloading}
            className="btn-3d-blue text-sm py-2 px-4 flex items-center gap-2"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Download Template
          </button>
          <span className="text-xs text-gray-500">
            Template columns: Name, Mobile Number, Subject, Test Date, Total Marks, Obtained Marks, Highest Marks
          </span>
        </div>
      </div>

      {/* Send Mode Selection */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-700 mb-4">Send Mode</h3>
        <div className="grid grid-cols-3 gap-3">
          {SEND_MODES.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setSendMode(mode.id)}
              className={`border-2 rounded-xl p-4 text-left transition-all ${
                sendMode === mode.id ? mode.activeColor : mode.color + ' hover:opacity-80'
              }`}
            >
              <div className={`mb-2 ${sendMode === mode.id ? 'text-blue-700' : 'text-gray-600'}`}>
                {mode.icon}
              </div>
              <p className="font-semibold text-gray-800 text-sm">{mode.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{mode.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Message Preset */}
      <MessagePreset
        selectedPresetId={presetId}
        onPresetChange={setPresetId}
        onTemplateChange={() => {}}
        schoolName={schoolName}
      />

      {/* Preview */}
      <StudentPreview students={students} />

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <XCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={18} className="text-green-600" />
            <span className="font-semibold text-green-700">Messages Sent!</span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-2xl font-bold text-gray-800">{result.total}</p>
              <p className="text-xs text-gray-500 mt-0.5">Total</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-2xl font-bold text-green-600">{result.sent}</p>
              <p className="text-xs text-gray-500 mt-0.5">Sent</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-red-200">
              <p className="text-2xl font-bold text-red-500">{result.failed}</p>
              <p className="text-xs text-gray-500 mt-0.5">Failed</p>
            </div>
          </div>
          {result.results && result.results.filter(r => r.status === 'failed').length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-red-600 mb-1">Failed students:</p>
              {result.results.filter(r => r.status === 'failed').map((r, i) => (
                <p key={i} className="text-xs text-red-500">{r.name} ({r.phone}) - {r.reason}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Send Button */}
      <button
        onClick={handleSend}
        disabled={sending || !file || students.length === 0}
        className={`w-full py-4 rounded-xl font-bold text-white text-lg flex items-center justify-center gap-3 transition-all transform
          ${sending || !file || students.length === 0
            ? 'bg-gray-300 cursor-not-allowed shadow-none'
            : 'bg-orange-500 hover:bg-orange-600 shadow-[0_6px_0_#c2410c] active:shadow-[0_2px_0_#c2410c] active:translate-y-1 cursor-pointer'
          }`}
      >
        {sending ? (
          <>
            <Loader2 size={22} className="animate-spin" />
            Sending Messages... ({students.length} students)
          </>
        ) : (
          <>
            <Send size={22} />
            Send Messages {students.length > 0 ? `(${students.length} students)` : ''}
          </>
        )}
      </button>
    </div>
  );
}
